import { queryOptions } from "@tanstack/react-query";

import {
  clearLegacyPreferences,
  readLegacyPreferences,
} from "@/lib/chantier-calendar/legacy-local-preferences";
import type {
  CalendarPreferences,
  CalendarPreferencesPatch,
} from "@/lib/chantier-calendar/preferences";
import { chantierApi } from "@/lib/queries/chantier-calendar";

const URL = "/api/user/calendrier-preferences";

export const calendarPreferencesKey = ["user", "calendar-preferences"] as const;

export function saveCalendarPreferences(patch: CalendarPreferencesPatch) {
  return chantierApi<{ preferences: CalendarPreferences }>(URL, {
    method: "PUT",
    body: JSON.stringify(patch),
  }).then((result) => result.preferences);
}

/** The person opened this chantier's calendar; the server dates it and keeps one a day. */
export async function recordChantierVisit(projectId: string): Promise<void> {
  await chantierApi<null>(URL, {
    method: "POST",
    body: JSON.stringify({ projectId }),
  });
}

/**
 * The account's preferences. The first read after the move off `localStorage`
 * also carries the old browser choice over, once, so nobody starts from the
 * defaults: while nothing is saved on the account, a browser value is saved
 * there and dropped; once something is saved, a leftover value is just dropped.
 */
async function loadCalendarPreferences(): Promise<CalendarPreferences> {
  const { preferences, saved } = await chantierApi<{
    preferences: CalendarPreferences;
    saved: boolean;
  }>(URL);

  const legacy = readLegacyPreferences();

  if (saved) {
    if (legacy) clearLegacyPreferences();

    return preferences;
  }

  if (!legacy) return preferences;

  try {
    const imported = await saveCalendarPreferences(legacy);
    clearLegacyPreferences();

    return imported;
  } catch {
    // The account could not be written: show what the person had and try again next time.
    return { ...preferences, ...legacy };
  }
}

export const calendarPreferencesQueryOptions = () =>
  queryOptions({
    queryKey: calendarPreferencesKey,
    queryFn: loadCalendarPreferences,
  });
